/**
 * Fonctions pour le positionnement et la création récursive des nœuds enfants
 */
import { createRoutingNode, createQueueNode, createEdge } from './nodeCreators';
import { getColor } from './nodeUtils';

/**
 * Crée récursivement tous les nœuds enfants manquants
 */
export const createMissingChildNodesRecursive = (
  parentKey,
  parentNode,
  routing,
  generatedNodes,
  generatedEdges,
  verticalGap,
  horizontalGap,
  onEdit,
  onDelete,
  onEdgeDelete,
  onEdgeLabelChange,
  chatFlowData,
  level = 0
) => {
  const routingItem = routing[parentKey];
  if (!routingItem || !parentNode) return;
  
  const indent = '  '.repeat(level);
  
  // Traiter Customer Choice → actions (PRIORITAIRE - vérifier d'abord la présence de customerChoiceActions)
  if (routingItem.customerChoiceActions && Object.keys(routingItem.customerChoiceActions).length > 0) {
    const actions = Object.entries(routingItem.customerChoiceActions);
    const actionCount = actions.length;
    
    actions.forEach(([actionKey, action], index) => {
      if (action.next?.routingName) {
        const childKey = action.next.routingName;
        const childNodeId = `routing-${childKey}`;
        
        const childExists = generatedNodes.find(n => n.id === childNodeId);
        
        if (!childExists && routing[childKey]) {
          const childRouting = routing[childKey];
          
          const offsetX = actionCount > 1 
            ? ((index - (actionCount - 1) / 2) * (horizontalGap / 2))
            : 0;
          
          const childNodeData = createRoutingNode(
            childKey,
            childRouting,
            parentNode.position.x + offsetX,
            parentNode.position.y + verticalGap,
            onEdit,
            onDelete,
            chatFlowData
          );
          
          generatedNodes.push(childNodeData.node);
          
          // RECURSION
          const childNode = generatedNodes.find(n => n.id === childNodeId);
          if (childNode) {
            createMissingChildNodesRecursive(
              childKey, childNode, routing, generatedNodes, generatedEdges,
              verticalGap, horizontalGap, onEdit, onDelete, onEdgeDelete, onEdgeLabelChange,
              chatFlowData, level + 1
            );
          }
        }
        
        // Créer l'edge
        const edgeId = `e-${parentNode.id}-${childNodeId}-${actionKey}`;
        if ((childExists || generatedNodes.find(n => n.id === childNodeId)) && !generatedEdges.find(e => e.id === edgeId)) {
          generatedEdges.push(createEdge(
            edgeId,
            parentNode.id,
            childNodeId,
            {
              sourceHandle: `source-${index}`,
              label: action.title || action.text,
              style: { stroke: '#8b5cf6', strokeWidth: 1.5, strokeDasharray: '5,5' },
              markerEnd: { type: 'arrowclosed', color: '#8b5cf6' },
              data: { onDelete: onEdgeDelete, onLabelChange: onEdgeLabelChange }
            }
          ));
          
          // RECURSION même si le nœud existait
          const childNode = generatedNodes.find(n => n.id === childNodeId);
          if (childNode && routing[childKey]) {
            createMissingChildNodesRecursive(
              childKey, childNode, routing, generatedNodes, generatedEdges,
              verticalGap, horizontalGap, onEdit, onDelete, onEdgeDelete, onEdgeLabelChange,
              chatFlowData, level + 1
            );
          }
        }
      }
    });
  }
  
  // Traiter Queue → next
  if (routingItem.type?.toLowerCase() === 'queue' && routingItem.next?.routingName) {
    const childKey = routingItem.next.routingName;
    const childNodeId = `routing-${childKey}`;
    
    const childExists = generatedNodes.find(n => n.id === childNodeId);
    
    if (!childExists && routing[childKey]) {
      const childRouting = routing[childKey];
      
      const childNodeData = createRoutingNode(
        childKey,
        childRouting,
        parentNode.position.x,
        parentNode.position.y + verticalGap,
        onEdit,
        onDelete,
        chatFlowData
      );
      
      generatedNodes.push(childNodeData.node);
      
      // RECURSION
      const childNode = generatedNodes.find(n => n.id === childNodeId);
      if (childNode) {
        createMissingChildNodesRecursive(
          childKey, childNode, routing, generatedNodes, generatedEdges,
          verticalGap, horizontalGap, onEdit, onDelete, onEdgeDelete, onEdgeLabelChange,
          chatFlowData, level + 1
        );
      }
    }
    
    // Créer l'edge
    const edgeId = `e-${parentNode.id}-${childNodeId}`;
    if ((childExists || generatedNodes.find(n => n.id === childNodeId)) && !generatedEdges.find(e => e.id === edgeId)) {
      generatedEdges.push(createEdge(
        edgeId,
        parentNode.id,
        childNodeId,
        {
          label: 'next',
          style: { stroke: '#3b82f6', strokeWidth: 2 },
          markerEnd: { type: 'arrowclosed', color: '#3b82f6' },
          data: { onDelete: onEdgeDelete, onLabelChange: onEdgeLabelChange }
        }
      ));
      
      // RECURSION même si le nœud existait
      const childNode = generatedNodes.find(n => n.id === childNodeId);
      if (childNode && routing[childKey]) {
        createMissingChildNodesRecursive(
          childKey, childNode, routing, generatedNodes, generatedEdges,
          verticalGap, horizontalGap, onEdit, onDelete, onEdgeDelete, onEdgeLabelChange,
          chatFlowData, level + 1
        );
      }
    }
  }
  
  // Traiter Form → next (seulement si pas de customerChoiceActions)
  else if (routingItem.type?.toLowerCase() === 'form' && routingItem.next?.routingName) {
    const childKey = routingItem.next.routingName;
    const childNodeId = `routing-${childKey}`;
    
    const childExists = generatedNodes.find(n => n.id === childNodeId);
    
    if (!childExists && routing[childKey]) {
      const childRouting = routing[childKey];
      
      const childNodeData = createRoutingNode(
        childKey,
        childRouting,
        parentNode.position.x,
        parentNode.position.y + verticalGap,
        onEdit,
        onDelete,
        chatFlowData
      );
      
      generatedNodes.push(childNodeData.node);
      
      // RECURSION
      const childNode = generatedNodes.find(n => n.id === childNodeId);
      if (childNode) {
        createMissingChildNodesRecursive(
          childKey, childNode, routing, generatedNodes, generatedEdges,
          verticalGap, horizontalGap, onEdit, onDelete, onEdgeDelete, onEdgeLabelChange,
          chatFlowData, level + 1
        );
      }
    }
    
    // Créer l'edge
    const edgeId = `e-${parentNode.id}-${childNodeId}`;
    if ((childExists || generatedNodes.find(n => n.id === childNodeId)) && !generatedEdges.find(e => e.id === edgeId)) {
      generatedEdges.push(createEdge(
        edgeId,
        parentNode.id,
        childNodeId,
        {
          label: 'next',
          style: { stroke: '#f59e0b', strokeWidth: 2 },
          markerEnd: { type: 'arrowclosed', color: '#f59e0b' },
          data: { onDelete: onEdgeDelete, onLabelChange: onEdgeLabelChange }
        }
      ));
      
      // RECURSION même si le nœud existait
      const childNode = generatedNodes.find(n => n.id === childNodeId);
      if (childNode && routing[childKey]) {
        createMissingChildNodesRecursive(
          childKey, childNode, routing, generatedNodes, generatedEdges,
          verticalGap, horizontalGap, onEdit, onDelete, onEdgeDelete, onEdgeLabelChange,
          chatFlowData, level + 1
        );
      }
    }
  }
};

/**
 * Repositionne récursivement les nœuds enfants sous leur parent
 */
export const repositionChildNodesRecursive = (
  parentKey,
  parentNode,
  chatFlowData,
  generatedNodes,
  verticalGap,
  horizontalGap,
  level = 0
) => {
  const routing = chatFlowData.routing || {};
  const routingItem = routing[parentKey];
  
  if (!routingItem || !parentNode) return;
  
  const indent = '  '.repeat(level);
  
  // Si c'est un Customer Choice avec actions (PRIORITAIRE)
  if (routingItem.customerChoiceActions && Object.keys(routingItem.customerChoiceActions).length > 0) {
    const actions = Object.values(routingItem.customerChoiceActions);
    const actionCount = actions.length;
    
    actions.forEach((action, index) => {
      if (action.next) {
        let childNodeId;
        let childKey;
        
        if (action.next.type === 'queue' && action.next.queueId) {
          childNodeId = `queue-${action.next.queueId}`;
          childKey = null;
        } else if (action.next.routingName) {
          childKey = action.next.routingName;
          childNodeId = `routing-${childKey}`;
        }
        
        if (childNodeId) {
          const childNode = generatedNodes.find(n => n.id === childNodeId);
          
          if (childNode) {
            const offsetX = actionCount > 1 
              ? ((index - (actionCount - 1) / 2) * (horizontalGap / 2))
              : 0;
            
            childNode.position = {
              x: parentNode.position.x + offsetX,
              y: parentNode.position.y + verticalGap
            };
            
            // RECURSION (seulement si c'est un routing)
            if (childKey) {
              repositionChildNodesRecursive(childKey, childNode, chatFlowData, generatedNodes, verticalGap, horizontalGap, level + 1);
            }
          }
        }
      }
    });
  }
  // Si c'est un Form avec next vers un routing (et pas de customerChoiceActions)
  else if (routingItem.type?.toLowerCase() === 'form' && routingItem.next?.routingName) {
    const childKey = routingItem.next.routingName;
    const childNodeId = `routing-${childKey}`;
    const childNode = generatedNodes.find(n => n.id === childNodeId);
    
    if (childNode) {
      childNode.position = {
        x: parentNode.position.x,
        y: parentNode.position.y + verticalGap
      };
      
      // RECURSION
      repositionChildNodesRecursive(childKey, childNode, chatFlowData, generatedNodes, verticalGap, horizontalGap, level + 1);
    }
  }
  
  // Si c'est une Queue avec next vers un routing
  if (routingItem.type?.toLowerCase() === 'queue' && routingItem.next?.routingName) {
    const childKey = routingItem.next.routingName;
    const childNodeId = `routing-${childKey}`;
    const childNode = generatedNodes.find(n => n.id === childNodeId);
    
    if (childNode) {
      childNode.position = {
        x: parentNode.position.x,
        y: parentNode.position.y + verticalGap
      };
      
      // RECURSION
      repositionChildNodesRecursive(childKey, childNode, chatFlowData, generatedNodes, verticalGap, horizontalGap, level + 1);
    }
  }
};
